// Tiny browser-storage-backed auth state used by the rest of the app.
//
// The FE has one source of truth for "am I signed in": the presence of
// a JWT under `localStorage['pdm.token']`. The api/client.ts fetch
// wrapper reads it via getStoredToken() and attaches it to every
// outgoing request. /v1/auth/login writes it; the user-menu Sign out
// action clears it. The router's beforeLoad gate redirects to /auth
// when it's missing.
//
// In MSW dev mode the token is just an opaque string — handlers don't
// verify it. Against the real backend the JWT carries the user's role,
// tenant, and expiry, and the api-gateway's RequireBearer middleware
// rejects requests without a valid one (returns 401).

const TOKEN_KEY = 'pdm.token';
const SLUG_KEY = 'pdm.tenant_slug';
const ROLE_KEY = 'pdm.dev.role'; // legacy MSW dev override; cleared on sign-out

export function getStoredToken(): string | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  try {
    const v = localStorage.getItem(TOKEN_KEY);
    return v && v.length > 0 ? v : undefined;
  } catch {
    return undefined;
  }
}

export function setStoredToken(token: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* private mode — non-fatal */
  }
}

export function clearStoredToken(): void {
  // Belt-and-braces: drop the JWT, the tenant slug, the legacy
  // dev-role override, and any pasted dev token so Sign out leaves
  // no trace.
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(SLUG_KEY);
      localStorage.removeItem('pdm.dev.token');
      localStorage.removeItem('pdm.dev.signedInEmail');
    } catch {
      /* ignore */
    }
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(ROLE_KEY);
      sessionStorage.removeItem('pdm.dev.viewer');
    } catch {
      /* ignore */
    }
  }
}

// getStoredOrgSlug / setStoredOrgSlug — companion to the token. The
// FE puts this in every per-org URL (/acme/fleet etc). The route
// guard validates that the slug in the URL matches the stored one
// and redirects on mismatch.
export function getStoredOrgSlug(): string | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  try {
    const v = localStorage.getItem(SLUG_KEY);
    return v && v.length > 0 ? v : undefined;
  } catch {
    return undefined;
  }
}

export function setStoredOrgSlug(slug: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SLUG_KEY, slug);
  } catch {
    /* private mode — non-fatal */
  }
}

export function isSignedIn(): boolean {
  return getStoredToken() !== undefined;
}
